// ===== BEGIN FILE: src/components/report-v2/sections/HouseBalanceSection.tsx =====
/**
 * "Activities to Balance Houses" section
 * Shows D1 house scores (blended planetary influence) as a detailed 12-row table.
 * Rows = houses H1–H12, columns = Sign, Lord, Residents, Aspectors, Bhava Bala, Impact, Status.
 * Followed by an actionable guide for houses needing attention.
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const HOUSE_NAMES: Record<number, string> = {
  1: 'Self & Vitality', 2: 'Wealth & Family', 3: 'Courage & Siblings',
  4: 'Home & Mother', 5: 'Intellect & Children', 6: 'Health & Service',
  7: 'Marriage & Trade', 8: 'Longevity & Occult', 9: 'Fortune & Dharma',
  10: 'Career & Status', 11: 'Gains & Networks', 12: 'Moksha & Charity',
};

const HOUSE_ICONS: Record<number, string> = {
  1: '☀️', 2: '💰', 3: '💪', 4: '🏠', 5: '🎨', 6: '⚔️',
  7: '💑', 8: '🔮', 9: '🙏', 10: '🏆', 11: '🌟', 12: '🙉️',
};

const SIGN_ABBR: Record<string, string> = {
  Aries: 'Ari', Taurus: 'Tau', Gemini: 'Gem', Cancer: 'Can',
  Leo: 'Leo', Virgo: 'Vir', Libra: 'Lib', Scorpio: 'Sco',
  Sagittarius: 'Sag', Capricorn: 'Cap', Aquarius: 'Aqu', Pisces: 'Pis',
};

const PLANET_ABBR: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

const ROLE_COLOR: Record<string, string> = {
  benefic: '#15803D', yoga_karaka: '#065F46', malefic: '#DC2626', neutral: '#374151',
};

const ordinal = (n: number) =>
  n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;

// ── Status chip logic ──────────────────────────────────────────────────────────

type StatusKey = 'excellent' | 'good' | 'fair' | 'needs_attention' | 'unknown';

// Lower number = higher priority in the activity card list (needs_attention first, excellent last)
const STATUS_PRIORITY: Record<StatusKey, number> = {
  needs_attention: 0, fair: 1, good: 2, excellent: 3, unknown: 4,
};

function computeStatus(impact: number, bhavaPct?: number | null, dashaImpact?: number | null, transitImpact?: number | null): StatusKey {
  // Natal component (65%): average of normalized planet impact + bhava bala
  const normalizedImpact = ((impact + 50) / 100) * 100;
  const natalComponent = bhavaPct != null ? (bhavaPct + normalizedImpact) / 2 : normalizedImpact;
  // Temporal component (35%): average of dasha + transit (default 50 = neutral)
  const dasha = dashaImpact ?? 50;
  const transit = transitImpact ?? 50;
  const temporalComponent = (dasha + transit) / 2;
  const combined = natalComponent * 0.65 + temporalComponent * 0.35;
  if (combined >= 75) return 'excellent';
  if (combined >= 60) return 'good';
  if (combined >= 30) return 'fair';
  return 'needs_attention';
}

const STATUS_CONFIG: Record<StatusKey, { label: string; color: string; bg: string }> = {
  excellent:       { label: '✅ Excellent',        color: '#14532D', bg: '#DCFCE7' },
  good:            { label: '🔵 Good',             color: '#1E3A8A', bg: '#DBEAFE' },
  fair:            { label: '🟡 Fair',             color: '#713F12', bg: '#FEF9C3' },
  needs_attention: { label: '⚠️ Needs Attention',  color: '#7F1D1D', bg: '#FEE2E2' },
  unknown:         { label: '—',                  color: '#9CA3AF', bg: '#F9FAFB' },
};

// ── CSS ───────────────────────────────────────────────────────────────────────

const PRINT_STYLE = `
  @media print {
    .house-balance-table { font-size: 6.5pt !important; }
    .house-balance-table td, .house-balance-table th { padding: 1.5pt 2pt !important; }
  }
`;

const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.70rem',
  fontFamily: 'inherit',
};

const TH: React.CSSProperties = {
  textAlign: 'center',
  padding: '5px 4px',
  fontWeight: 700,
  fontSize: '0.70rem',
  color: '#1F2937',
  background: '#F9FAFB',
  borderBottom: '2px solid #E5E7EB',
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '4px 4px',
  fontSize: 'inherit',
  verticalAlign: 'middle',
  borderBottom: '1px solid #F3F4F6',
};

// ── Component ─────────────────────────────────────────────────────────────────

export const HouseBalanceSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { houseScores, birthDetails, houseLords, houseActivities, lordDusthanaPenalties } = data;

  const cusps = birthDetails.houseCusps ?? [];
  const planetsInHouses = birthDetails.planetsInHouses ?? [];
  const aspects = birthDetails.planetaryAspects ?? [];

  // Build per-house view model
  const houseRows = Array.from({ length: 12 }, (_, i) => {
    const h = i + 1;
    const cusp = cusps.find(c => c.house === h);
    const sign = cusp?.sign ?? '—';
    const bhavaPct = cusp?.bhavaPct;
    const dashaImpact = cusp?.dashaImpact ?? null;
    const transitImpact = cusp?.transitImpact ?? null;
    const impact = Math.round(houseScores[h] ?? 0);
    const lordInfo = houseLords?.[h];
    const lord = lordInfo?.lord ?? '—';
    const lordHouse = lordInfo?.lordHouse;

    // Residents from planetsInHouses
    const resEntry = planetsInHouses.find(p => p.house === h);
    const residents = resEntry?.planets ?? [];

    // Aspectors: filter planetary aspects targeting this house, exclude residents
    const residentNames = new Set(residents.map(r => r.name));
    const aspectorNames = Array.from(
      new Set(
        aspects
          .filter(a => a.toHouse === h && !residentNames.has(a.from))
          .map(a => a.from)
      )
    );

    // Combined (4-metric, 65/35 weighted, same formula as HouseScoresSection)
    const normalizedImpact = ((impact + 50) / 100) * 100;
    const natalComponent = bhavaPct != null ? (bhavaPct + normalizedImpact) / 2 : normalizedImpact;
    const temporalComponent = ((dashaImpact ?? 50) + (transitImpact ?? 50)) / 2;
    const combined = Math.round(natalComponent * 0.65 + temporalComponent * 0.35);
    const affliction = lordDusthanaPenalties?.[h] ?? null;
    const baseStatus = computeStatus(impact, bhavaPct, dashaImpact, transitImpact);
    const status: StatusKey = (affliction && !affliction.jupiterProtected) ? 'needs_attention' : baseStatus;
    // Activities follow the lord: if H1 lord sits in H10, perform H10 activities to balance H1
    const activitySourceHouse = lordHouse ?? h;
    const activities = houseActivities?.[activitySourceHouse] ?? null;
    return { h, sign, bhavaPct, impact, combined, lord, lordHouse, activitySourceHouse, residents, aspectorNames, status, activities, affliction };
  });

  // All 12 houses sorted by priority (needs_attention first, excellent last)
  const sortedActivityRows = [...houseRows].sort(
    (a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status] || a.h - b.h
  );

  const sortedByCombined = [...houseRows].sort((a, b) => b.combined - a.combined);
  const topHouses = sortedByCombined.slice(0, 3);
  const weakHouses = sortedByCombined.slice(-3).reverse();

  return (
    <ReportSection
      title="Activities to Balance Houses"
      icon="🏛️"
      pageBreakBefore
      navigateTab="calculator"
      navigateLabel="View House Analysis →"
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
    >
      <style>{PRINT_STYLE}</style>

      <Typography
        level="body-sm"
        sx={{ mb: 1, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.6 }}
      >
        In Vedic astrology, the 12 houses represent distinct areas of life — from self and wealth to career, relationships, and spiritual growth. Each house is governed by a zodiac sign and a planetary lord, and its strength reflects how much support or challenge that life area carries in your chart.
      </Typography>
      <Typography
        level="body-sm"
        sx={{ mb: 2, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.6 }}
      >
        Each house shows its sign, lord placement, resident and aspecting planets, Bhava Bala and planetary impact.
        The <strong>Combined</strong> column and <strong>Status</strong> badge use a four-metric weighted blend:
        <strong> Bhava Bala + Planet Impact</strong> (natal, 65%) and <strong>Dasha + Transit Impact</strong> (live, 35%),
        using the scale: ≥75% Excellent • 60–74% Good • 30–59% Fair • &lt;30% Needs Attention.
      </Typography>

      {/* ── MAIN TABLE ───────────────────────────────────────────────── */}
      <Box sx={{
        overflowX: 'auto',
        border: '1px solid', borderColor: 'divider', borderRadius: 'sm',
        mb: 3,
        '@media print': { overflowX: 'visible', border: 'none' },
      }}>
        <table className="house-balance-table" style={TABLE_STYLE}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: 'left', width: 130 }}>House</th>
              <th style={TH}>Sign</th>
              <th style={TH}>Lord → In</th>
              <th style={{ ...TH, textAlign: 'left' }}>Residents</th>
              <th style={{ ...TH, textAlign: 'left' }}>Aspectors</th>
              <th style={TH}>Bhava Bala</th>
              <th style={TH}>Planet Impact</th>
              <th style={{ ...TH, fontWeight: 800 }}>Combined</th>
              <th style={TH}>Status</th>
            </tr>
          </thead>
          <tbody>
            {houseRows.map(({ h, sign, bhavaPct, impact, combined, lord, lordHouse, residents, aspectorNames, status, affliction }, idx) => {
              const sc = STATUS_CONFIG[status];
              const bhavaColor =
                bhavaPct == null ? '#9CA3AF' :
                bhavaPct >= 75 ? '#15803D' :
                bhavaPct >= 60 ? '#1D4ED8' :
                bhavaPct >= 30 ? '#CA8A04' : '#DC2626';
              const impColor =
                impact >= 5 ? '#15803D' :
                impact >= 0 ? '#4D7C0F' :
                impact >= -5 ? '#92400E' : '#DC2626';
              const combinedRounded = Math.round(combined);
              const combinedColor =
                combinedRounded >= 75 ? '#15803D' :
                combinedRounded >= 60 ? '#1D4ED8' :
                combinedRounded >= 30 ? '#CA8A04' : '#DC2626';

              return (
                <tr key={h} style={{ background: idx % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                  {/* House # + Name */}
                  <td style={{ ...TD, fontWeight: 700 }}>
                    <span style={{ color: '#5B21B6', fontSize: '0.78rem' }}>H{h}</span>
                    <span style={{ color: '#6B7280', fontWeight: 400, fontSize: '0.65rem', marginLeft: 4 }}>
                      {HOUSE_NAMES[h]}
                    </span>
                  </td>

                  {/* Sign abbreviation */}
                  <td style={{ ...TD, textAlign: 'center', color: '#374151' }}>
                    {SIGN_ABBR[sign] ?? sign}
                  </td>

                  {/* Lord → lordHouse */}
                  <td style={{ ...TD, textAlign: 'center', fontWeight: 600, color: '#1D4ED8', whiteSpace: 'nowrap' }}>
                    {PLANET_ABBR[lord] ?? lord}
                    {lordHouse ? (
                      <span style={{ color: '#6B7280', fontWeight: 400 }}> →H{lordHouse}</span>
                    ) : null}
                  </td>

                  {/* Residents */}
                  <td style={{ ...TD }}>
                    {residents.length === 0 ? (
                      <span style={{ color: '#D1D5DB' }}>—</span>
                    ) : (
                      residents.map((r, ri) => (
                        <span key={ri} style={{
                          display: 'inline-block',
                          marginRight: 3,
                          padding: '1px 4px',
                          borderRadius: 3,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          background: `${ROLE_COLOR[r.functionalNature] || '#374151'}18`,
                          color: ROLE_COLOR[r.functionalNature] || '#374151',
                          border: `1px solid ${ROLE_COLOR[r.functionalNature] || '#374151'}40`,
                        }}>
                          {PLANET_ABBR[r.name] ?? r.name}
                        </span>
                      ))
                    )}
                  </td>

                  {/* Aspectors */}
                  <td style={{ ...TD }}>
                    {aspectorNames.length === 0 ? (
                      <span style={{ color: '#D1D5DB' }}>—</span>
                    ) : (
                      aspectorNames.map((pName, ai) => (
                        <span key={ai} style={{
                          display: 'inline-block',
                          marginRight: 3,
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          color: '#374151',
                        }}>
                          {PLANET_ABBR[pName] ?? pName}
                        </span>
                      ))
                    )}
                  </td>

                  {/* Bhava Bala */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    {bhavaPct != null ? (
                      <span style={{ fontWeight: 700, color: bhavaColor }}>{bhavaPct}%</span>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>—</span>
                    )}
                  </td>

                  {/* Planet Impact */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, color: impColor }}>
                      {impact > 0 ? '+' : ''}{impact}%
                    </span>
                    {affliction && (() => {
                      const off = (affliction.lordHouse - h + 12) % 12;
                      const nth = off === 5 ? '6th' : off === 7 ? '8th' : '12th';
                      const jp = affliction.jupiterProtected;
                      return (
                        <div
                          title={jp ? `${affliction.reason} — protected by Jupiter` : affliction.reason}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '2px', marginTop: '2px',
                            background: jp ? '#f0fdf4' : affliction.severity === 'critical' ? '#fef2f2' : affliction.severity === 'severe' ? '#fff7ed' : '#fffbeb',
                            border: `1px solid ${jp ? '#86efac' : affliction.severity === 'critical' ? '#fca5a5' : affliction.severity === 'severe' ? '#fdba74' : '#fcd34d'}`,
                            borderRadius: '3px', padding: '1px 3px', cursor: 'help', whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{ fontSize: '7px', lineHeight: 1 }}>{jp ? '\u2643' : '\u26a0'}</span>
                          <span style={{
                            fontSize: '0.55rem', fontWeight: 700, lineHeight: 1,
                            color: jp ? '#15803d' : affliction.severity === 'critical' ? '#b91c1c' : affliction.severity === 'severe' ? '#c2410c' : '#b45309',
                          }}>
                            {jp ? `${nth} from own \u2643` : `${nth} from own`}&nbsp;({affliction.penalty})
                          </span>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Combined */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <span style={{ fontWeight: 800, color: combinedColor }}>{combinedRounded}%</span>
                  </td>

                  {/* Status chip */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: '0.63rem',
                      fontWeight: 700,
                      background: sc.bg,
                      color: sc.color,
                    }}>
                      {sc.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {/* ── LEGEND ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3, fontSize: '0.72rem', color: 'text.secondary' }}>
        {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'unknown').map(([, sc]) => (
          <span key={sc.label} style={{
            padding: '2px 7px', borderRadius: 4, background: sc.bg,
            color: sc.color, fontWeight: 700, fontSize: '0.68rem',
          }}>
            {sc.label}
          </span>
        ))}
        <span style={{ fontSize: '0.68rem', color: '#6B7280', alignSelf: 'center' }}>
          &nbsp; Residents = planets <em>in</em> house &nbsp;·&nbsp; Aspectors = planets aspecting from outside
        </span>
      </Box>

      {/* ── STRONGEST / AREAS FOR GROWTH ─────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        {/* Strongest */}
        <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1.5px solid #bbf7d0', background: 'linear-gradient(135deg, #f0fdf4, #ffffff)', boxShadow: '0 2px 8px #16a34a14' }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #16a34a1a, #16a34a06)', borderBottom: '1px solid #bbf7d0' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#15803d' }}>✨ Strongest Houses</Typography>
          </Box>
          <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {topHouses.map(({ h, combined }, i) => (
              <Box key={h} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', width: '14px' }}>{i + 1}.</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#1f2937' }}>{HOUSE_ICONS[h]} H{h} — {HOUSE_NAMES[h]}</Typography>
                </Box>
                <Box sx={{ background: '#dcfce7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d' }}>{Math.round(combined)}%</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {/* Areas for Growth */}
        <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1.5px solid #fed7aa', background: 'linear-gradient(135deg, #fffbeb, #ffffff)', boxShadow: '0 2px 8px #d9770614' }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #d977061a, #d9770606)', borderBottom: '1px solid #fed7aa' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#b45309' }}>⚡ Areas for Growth</Typography>
          </Box>
          <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {weakHouses.map(({ h, combined }, i) => (
              <Box key={h} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309', width: '14px' }}>{i + 1}.</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#1f2937' }}>{HOUSE_ICONS[h]} H{h} — {HOUSE_NAMES[h]}</Typography>
                </Box>
                <Box sx={{ background: '#fef3c7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309' }}>{Math.round(combined)}%</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── ACTION GUIDE ─────────────────────────────────────────────── */}
      <Box>
        <Typography level="title-sm" sx={{ mb: 0.5, color: '#7C3AED', fontWeight: 700, fontSize: '0.85rem' }}>
          🎯 House-by-House Activities (Priority Order)
        </Typography>
        <Typography level="body-xs" sx={{ mb: 1.5, color: '#6B7280', fontSize: '0.72rem' }}>
          Houses ordered by priority — ⚠️ Needs Attention first, ✅ Excellent last. Activities from classical Jyotish guidelines.
        </Typography>

        {/* All 12 houses, sorted: intense → weak → limited → neutral → strong → ideal */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1.5,
          '@media print': { gridTemplateColumns: 'repeat(2, 1fr)' },
        }}>
          {sortedActivityRows.map(({ h, status, lord, lordHouse, activitySourceHouse, activities, affliction }) => {
            const sc = STATUS_CONFIG[status];
            const lordRouted = lordHouse !== undefined && lordHouse !== h;
            return (
              <Box key={h} sx={{
                p: 1.5,
                borderRadius: 'sm',
                borderLeft: `4px solid ${sc.color}`,
                background: sc.bg + '40',
                border: `1px solid ${sc.color}30`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography level="title-sm" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.3 }}>
                    H{h} — {HOUSE_NAMES[h]}
                  </Typography>
                  <span style={{
                    padding: '1px 5px', borderRadius: 3,
                    fontSize: '0.63rem', fontWeight: 700,
                    background: sc.bg, color: sc.color,
                    border: `1px solid ${sc.color}60`,
                    whiteSpace: 'nowrap',
                  }}>
                    {sc.label}
                  </span>
                </Box>
                {/* Lord-routing tag */}
                <Typography level="body-xs" sx={{
                  mb: 0.5, fontSize: '0.66rem', color: '#6B7280', fontStyle: 'italic',
                }}>
                  {lordRouted
                    ? `${lord} (H${h} lord) sits in H${activitySourceHouse} → H${activitySourceHouse} activities balance H${h}`
                    : `${lord} (H${h} lord) sits in H${h} → H${h} own activities apply`
                  }
                </Typography>
                {/* Dusthana warning badge */}
                {affliction && (() => {
                  const off = (affliction.lordHouse - h + 12) % 12;
                  const nth = off === 5 ? '6th' : off === 7 ? '8th' : '12th';
                  const jp = affliction.jupiterProtected;
                  return (
                    <Box
                      title={jp ? `${affliction.reason} — protected by Jupiter aspect` : affliction.reason}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px', mb: 0.75, cursor: 'help',
                        bgcolor: jp ? '#f0fdf4' : affliction.severity === 'critical' ? '#fef2f2' : affliction.severity === 'severe' ? '#fff7ed' : '#fffbeb',
                        border: `1px solid ${jp ? '#86efac' : affliction.severity === 'critical' ? '#fca5a5' : affliction.severity === 'severe' ? '#fdba74' : '#fcd34d'}`,
                        borderRadius: '4px', px: 0.75, py: '2px',
                      }}
                    >
                      <Typography sx={{ fontSize: '8px', lineHeight: 1 }}>{jp ? '\u2643' : '\u26a0'}</Typography>
                      <Typography sx={{
                        fontSize: '0.63rem', fontWeight: 700, lineHeight: 1,
                        color: jp ? '#15803d' : affliction.severity === 'critical' ? '#b91c1c' : affliction.severity === 'severe' ? '#c2410c' : '#b45309',
                      }}>
                        {jp
                          ? `Lord in ${nth} from own — Jupiter protected`
                          : `Lord in dusthana (${nth} from own) — Needs Attention`
                        }
                      </Typography>
                    </Box>
                  );
                })()}
                {activities ? (
                  <Typography level="body-sm" sx={{
                    color: 'text.primary',
                    fontSize: '0.75rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {activities}
                  </Typography>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', fontSize: '0.75rem', fontStyle: 'italic' }}>
                    Activities not available.
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* How to read */}
      <Box sx={{
        mt: 2, p: 1.5,
        background: '#FFF7ED',
        borderLeft: '3px solid #F59E0B',
        borderRadius: '0 4px 4px 0',
        fontSize: '0.75rem',
        color: '#78350F',
        lineHeight: 1.6,
        '@media print': { fontSize: '7pt' },
      }}>
        <strong>How to read:</strong> <strong>Status</strong> = average of Bhava Bala &amp; Blended Impact.
        <strong>≥75% = Excellent</strong>, <strong>60–74% = Good</strong>, <strong>30–59% = Fair</strong>, <strong>&lt;30% = Needs Attention</strong>.
        Cards sorted by priority — <strong>⚠️ Needs Attention</strong> houses appear first;
        <strong>✅ Excellent</strong> houses appear last (maintain them).
        Planet abbreviations: Su=Sun, Mo=Moon, Ma=Mars, Me=Mercury, Ju=Jupiter, Ve=Venus, Sa=Saturn, Ra=Rahu, Ke=Ketu.
      </Box>
    </ReportSection>
  );
};
